import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { F, C, btn, inp } from '../../config/styles';
import { TenantAdminView } from '../views/TenantAdminView';

export function AdminPortal() {
  const [user,        setUser]        = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [isAdmin,     setIsAdmin]     = useState(false);

  const checkAdmin = async (uid) => {
    const { data } = await supabase
      .from('profiles')
      .select('is_platform_admin')
      .eq('id', uid)
      .maybeSingle();
    setIsAdmin(data?.is_platform_admin === true);
  };

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) await checkAdmin(session.user.id);
      setAuthLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_, session) => {
      setUser(session?.user ?? null);
      if (session?.user) await checkAdmin(session.user.id);
      else setIsAdmin(false);
    });
    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (authLoading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: F.body, color: C.muted }}>
      Loading…
    </div>
  );

  if (!user) return <AdminLogin />;

  if (!isAdmin) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: F.body }}>
      <div style={{ textAlign: 'center', maxWidth: 360 }}>
        <div style={{ fontFamily: F.display, fontSize: 22, color: C.text, marginBottom: 8 }}>Access denied</div>
        <div style={{ fontSize: 13, color: C.muted, marginBottom: 24 }}>
          {user.email} is not a platform admin. Contact support if this is unexpected.
        </div>
        <button style={{ ...btn('ghost', { fontSize: 13, color: C.muted }), textDecoration: 'underline' }}
          onClick={() => supabase.auth.signOut()}>
          Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: C.white, fontFamily: F.body }}>
      <div style={{ height: 52, background: C.white, borderBottom: `1px solid ${C.e8}`, display: 'flex', alignItems: 'center', padding: '0 20px', gap: 12, position: 'sticky', top: 0, zIndex: 20 }}>
        <span style={{ fontFamily: F.display, fontSize: 19, color: C.text, flex: 1 }}>Range Builder Admin</span>
        <span style={{ fontSize: 12, color: C.muted }}>{user.email}</span>
        <button
          style={{ ...btn('ghost', { fontSize: 12, padding: '5px 10px', border: `1px solid ${C.e8}`, borderRadius: 6, color: C.muted }) }}
          onClick={() => supabase.auth.signOut()}
        >
          Sign out
        </button>
      </div>
      <TenantAdminView />
    </div>
  );
}

function AdminLogin() {
  const [email,   setEmail]   = useState('');
  const [sent,    setSent]    = useState(false);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    const { error: err } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin },
    });
    setLoading(false);
    if (err) { setError(err.message); return; }
    setSent(true); setCooldown(60);
  };

  return (
    <div style={{ minHeight: '100vh', background: C.fa, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: F.body }}>
      <div style={{ background: C.white, border: `1px solid ${C.e8}`, borderRadius: 12, padding: 40, width: 360 }}>
        <div style={{ fontFamily: F.display, fontSize: 22, color: C.text, marginBottom: 4 }}>Range Builder Admin</div>
        <div style={{ fontSize: 13, color: C.muted, marginBottom: 28 }}>Platform administrator access only</div>
        {sent ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>✉️</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 8 }}>Check your email</div>
            <div style={{ fontSize: 13, color: C.muted }}>Magic link sent to <strong>{email}</strong></div>
            <button
              style={{ ...btn('ghost', { marginTop: 20, fontSize: 12, color: C.muted }), opacity: cooldown > 0 ? 0.5 : 1 }}
              disabled={cooldown > 0}
              onClick={() => setSent(false)}
            >
              {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend or use different email'}
            </button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <input
              type="email" required
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="admin@yourdomain.com"
              style={{ ...inp({ width: '100%', padding: '10px 12px', fontSize: 14, boxSizing: 'border-box', marginBottom: 12 }) }}
            />
            {error && <div style={{ fontSize: 12, color: '#e53e3e', marginBottom: 10 }}>{error}</div>}
            <button type="submit" disabled={loading || cooldown > 0}
              style={btn('primary', { width: '100%', padding: '10px 0', fontSize: 14, justifyContent: 'center' })}>
              {loading ? 'Sending…' : 'Send magic link'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
